import { Component, FunctionalComponent, h } from 'preact'
import Icon from '../assets'
import Phrase from '../../libs/translationsUtils/phrase'
import UserAvatar from '../User/UserAvatar'
import LazyLoader from '../lazy-loading/LazyLoader'
import tracker from '../../components/tracker/publish'
import { z } from 'zod'
import { Context, GetServerSideProps } from '../../server-middleware/context'
import { getGroups } from '../../server-middleware/groupRepository'
import UserAPI from '../../services/api'
import Button from '../../control-components/ui-components/Button'
import Axios from 'axios'
import { csrfToken } from '../utils/csrfToken'
import Message from '../design-system-components/message'
import { trackRequestToJoinPrivateGroup } from '../utils/mixpanelEventsFromDestination'
import { ModulesContext } from '../../utils/globals'
import { CurrentUserContext } from '../../server-middleware/userContext'
import { GroupTags } from './GroupTags'

export const ORIGIN_GROUP_OVERVIEW = 'JoinViaGroupOverview'
export const ORIGIN_GROUP_WIDGET = 'JoinViaGroupWidget'

interface Member {
  id: number
  url: string
  avatar: string
  name: string
}

export interface Group {
  id: number
  title: string
  url: string
  totalMembers: number
  totalTopics: number
  type: number
  recentMembers: Member[]
  thumbnail: string
  tags: string[]
  isUserMember?: boolean
  membershipStatus?: membershipTypes
}

interface GroupItemProps extends Group {
  isUserMember?: boolean
  displayUserStatus?: boolean
  isEvenCount: boolean
  showGroupMeta: boolean
  onMembershipStatusChanged?: (newStatus: membershipTypes) => void
  origin?: string
}

interface GroupItemState {
  isLoading: boolean
  membershipStatus?: membershipTypes
}

const PUBLIC_GROUP_TYPE = 4
const PRIVATE_GROUP_TYPE = 5
const HIDDEN_GROUP_TYPE = 6

export const widgetName = 'group_overview' as const

export const config = z.undefined()

export const getServerSideProps: GetServerSideProps<undefined, Props> = async (ctx: Context): Promise<Props> => {
  return await ctx.cache(`user_${ctx.userId}_groups`, async () => {
    function isUser (u: unknown): u is Member {
      return !!u
    }

    const api = new UserAPI(ctx)
    const groups = await getGroups(ctx)
    const users = await api.getUsers(groups.reduce<number[]>((acc, g) => acc.concat(g.group_members.map(u => u.id)), []))

    const groupsDto = groups.map(g => {
      const recentMembers: Member[] = g.group_members.map(m => {
        const u = users[m.id]
        if (!u) return null
        return {
          id: m.id,
          url: `/member/${m.id}`,
          avatar: u.avatar,
          name: u.username
        }
      }).filter(isUser)

      return {
        id: g.forumid,
        title: g.title,
        url: `/groups/${g.forumid}`,
        recentMembers,
        totalTopics: g.threadcount,
        type: g.type || 0,
        totalMembers: g.group_members.length,
        isCurrentUserMember: ctx.userId && g.group_members.map(m => m.id).includes(ctx.userId),
        thumbnail: g.thumbnail || '',
        tags: g.group_tags_groups.map(gtg => gtg.group_tags.name || '')
      }
    })

    return {
      groups: {
        myGroups: groupsDto.filter(g => g.isCurrentUserMember),
        otherGroups: groupsDto.filter(g => !g.isCurrentUserMember)
      }
    }
  }, 60)
}

const GroupUsersTypeMeta: FunctionalComponent<GroupItemProps> = (group: GroupItemProps) => {
  return (
    <div>
      <div class='group-overview-item__meta group-overview-item__meta--members-total text--meta'>
        {group.type === PUBLIC_GROUP_TYPE
          ? (
            <div style='display: flex'>
              <Icon width={20} height={20} icon='people' /> <span class='group-overview-item__meta-text'>{group.totalMembers} <Phrase p='Members' /></span>
            </div>
            )
          : null}

        {group.type === PRIVATE_GROUP_TYPE
          ? (
            <div style='display: flex'>
              <Icon width={20} height={20} icon='lock' /><span className='group-overview-item__meta-text'><Phrase p='groups.private.group' /></span>
            </div>
            )
          : null}

        {group.type === HIDDEN_GROUP_TYPE
          ? (
            <div style='display: flex'>
              <Icon width={20} height={20} icon='eye-cross' /><span className='group-overview-item__meta-text'><Phrase p='groups.hidden.group' /></span>
            </div>
            )
          : null}
      </div>
      <div class='group-overview-item__meta group-overview-item__meta--topics-total text--meta'>
        <Icon width={20} height={20} icon='conversation' /> <span class='group-overview-item__meta-text'>
          {
            group.totalTopics === 0
              ? (
                <Phrase p='groups.no.topics.yet' />
                )
              : (
                <span>{group.totalTopics} <Phrase p='Topic|Topics' count={{ n: group.totalTopics.toString() }} /></span>
                )
          }
        </span>
      </div>
    </div>
  )
}

export class GroupItem extends Component<GroupItemProps, GroupItemState> {
  constructor (props: GroupItemProps) {
    super(props)

    this.state = {
      isLoading: false,
      membershipStatus: props.membershipStatus
    }

    this.joinGroup = this.joinGroup.bind(this)
    this.getGroupType = this.getGroupType.bind(this)
  }

  async joinGroup () {
    if (!window.inSidedData.user.userid) {
      const event = new window.CustomEvent('GuestUserAction')
      window.dispatchEvent(event)
      return
    }

    this.setState({ isLoading: true })

    try {
      const { data } = await Axios.post(`/group/${this.props.id}/join?joinOrigin=${this.props.origin}`, { csrfToken: csrfToken() })
      this.setState({ isLoading: false })
      if (typeof this.props.onMembershipStatusChanged === 'function') {
        this.props.onMembershipStatusChanged(data.membershipStatus)
        this.setState({ membershipStatus: data.membershipStatus })
      }

      if (this.props.type === PRIVATE_GROUP_TYPE) {
        trackRequestToJoinPrivateGroup(
          window.inSidedData.page.url === '/groups' ? 'RequestViaGroupOverview' : 'RequestViaGroupWidget',
          Number(this.props.id)
        )
      }
    } catch (e) {
      this.setState({ isLoading: false })
    }
  }

  getGroupType () {
    if (this.props.type === PRIVATE_GROUP_TYPE) {
      return 'private'
    }
    if (this.props.type === HIDDEN_GROUP_TYPE) {
      return 'hidden'
    }
    return 'public'
  }

  render (group: GroupItemProps, { isLoading, membershipStatus }: GroupItemState) {
    return (
      <ModulesContext.Consumer>
        {(modules) =>
          <CurrentUserContext.Consumer>
            {(user) => {
              return (
                <a class={`group-overview-item card-widget-wrapper ${group.isEvenCount ? 'group-overview-item--even' : ''}`} href={group.url}>
                  <LazyLoader type='background' src={group.thumbnail} classNames='group-overview-item__top'>
                    {
              !modules.includes('NewUICardGroupOverview') && group.type === PUBLIC_GROUP_TYPE
                ? (
                  <div className='group-overview-item__top-content'>
                    {
                  group.recentMembers.concat([]).reverse().map(member => {
                    if (member.id === user.id) {
                      return null
                    }
                    return (
                      <div key={member.id} className='group-overview-item__member'>
                        <span className='group-overview-item__member-link'>
                          <UserAvatar
                            user={member}
                            extraClass='avatar avatar--S'
                            showRank
                          />
                        </span>
                      </div>
                    )
                  })
                  }
                    {
                  group.isUserMember
                    ? <div key='me' className='group-overview-item__member'>
                      <span className='group-overview-item__member-link'>
                        <Icon icon='checkmark' height={30} width={30} />
                      </span>
                    </div>
                    : null
                  }
                  </div>
                  )
                : null
              }
                  </LazyLoader>
                  <div class={`group-overview-item__bottom ${group.showGroupMeta ? '' : 'group-overview-item__bottom--without-meta'}`}>
                    <h4 class='card-widget-title'>{group.title}</h4>
                    {!modules.includes('NewUICardGroupOverview') ? <GroupUsersTypeMeta {...group} /> : null}
                    {modules.includes('NewUICardGroupOverview') && group.showGroupMeta
                      ? (
                        <div class='group-overview-item__meta group-overview-item__meta--members-total text--meta'>
                          <div style='display: flex'>
                            <span style='color: #6B707E'><Phrase p={`groups.${this.getGroupType()}.group`} /> • {group.totalMembers} <Phrase p='Members' /></span>
                          </div>
                        </div>
                        )
                      : null}
                    {
              modules.includes('NewUICardGroupOverview') && (!group.isUserMember || group.displayUserStatus)
                ? (
                  <div className='join-group-btn-wrapper'>
                    {
                  membershipStatus === 'not_member'
                    ? (
                      <Button
                        loading={isLoading}
                        medium
                        primary
                        type='button'
                        onClick={(e) => { this.joinGroup(); e.stopPropagation(); e.preventDefault() }}
                      ><Phrase p={group.type === PUBLIC_GROUP_TYPE ? 'groups.join.group' : 'groups.request.to.join'} /></Button>
                      )
                    : (
                      <Button
                        medium
                        class='no-hover'
                        secondary
                        type='button'
                      ><Phrase p={membershipStatus === 'joined' ? 'groups.you.are.member' : 'groups.request.join.group'} /></Button>
                      )
                  }
                  </div>
                  )
                : null
              }
                  </div>
                </a>
              )
            }}
          </CurrentUserContext.Consumer>}
      </ModulesContext.Consumer>
    )
  }
}

interface Props {
  groups: {
    myGroups: Group[]
    otherGroups: Group[]
  }
}

export type membershipTypes = 'joined' | 'requested' | 'not_member'

interface State {
  selectedTags: string[]
  tagCount: Record<string, number>
  successMessageFor: null | membershipTypes
  joinedGroupTitle: string
}

class GroupsOverview extends Component<Props, State> {
  constructor (props: Props) {
    super()
    const tagCount: State['tagCount'] = {}

    props.groups.otherGroups.forEach((group: Group) => {
      group.tags.forEach(tag => {
        if (tagCount[tag]) {
          tagCount[tag]++
        } else {
          tagCount[tag] = 1
        }
      })
    })

    this.state = {
      selectedTags: [],
      tagCount,
      successMessageFor: null,
      joinedGroupTitle: ''
    }

    this.selectTag = this.selectTag.bind(this)
  }

  selectTag (item: string) {
    if (this.state.selectedTags.includes(item)) {
      this.setState({
        selectedTags: this.state.selectedTags.filter(tag => tag !== item)
      })
    } else {
      this.setState({
        selectedTags: [...this.state.selectedTags, item]
      })
    }

    tracker.trackEvent('userevent', {
      event_source: 'control', // mixpanel-forwarder is ignoring any other type of event
      type: 'Group overview tag clicked',
      name: item
    })
  }

  render ({ groups }: Props, { selectedTags, successMessageFor, joinedGroupTitle }: State) {
    const { myGroups, otherGroups } = groups

    const allGroupTags = otherGroups.reduce<string[]>((acc, group) => {
      group.tags.forEach(tag => {
        if (!acc.includes(tag)) {
          acc.push(tag)
        }
      })
      return acc
    }, [])

    const filteredGroups = otherGroups.filter(group => {
      if (!selectedTags.length) return true
      return selectedTags.some(tag => group.tags.includes(tag))
    })

    return (
      <div class='group-overview-wrapper'>
        {
          successMessageFor !== null
            ? (
              <Message
                variant='success'
                onDismiss={() => this.setState({ successMessageFor: null })}
                timer={5000}
              >
                <Phrase p={successMessageFor === 'joined' ? 'group.joined.successfully' : 'group.join.request.is.sent'} count={{ g: joinedGroupTitle }} />
              </Message>
              )
            : null
        }
        <div class='sitewidth text-center'>
          <h1 class='group-overview-wrapper-title'><Phrase p='groups' /></h1>
        </div>

        {myGroups.length
          ? <div class='groups--mine'>
            <h4 class='group-overview-title'><Phrase p='groups.my.groups' /></h4>

            <div class='group-overview'>
              {
            myGroups.map(group => (
              <GroupItem
                key={group.id}
                isEvenCount={myGroups.length === 2 || myGroups.length === 4}
                isUserMember
                {...group}
                showGroupMeta={false}
              />
            ))
          }
            </div>
          </div>
          : null}
        {
        otherGroups.length
          ? (
            <div className='groups--other'>
              <h4 className='group-overview-title'><Phrase p='groups.find.new.group' /></h4>
              <GroupTags
                tags={allGroupTags}
                selectedTags={selectedTags}
                onTagClick={tagLabel => this.selectTag(tagLabel)}
              />

              <div className='group-overview'>
                {
                  filteredGroups.map(group => (
                    <GroupItem
                      key={group.id}
                      onMembershipStatusChanged={(newMembershipStatus: membershipTypes) => (
                        this.setState({
                          successMessageFor: newMembershipStatus,
                          joinedGroupTitle: group.title
                        })
                      )}
                      isEvenCount={filteredGroups.length === 2 || filteredGroups.length === 4}
                      origin={ORIGIN_GROUP_OVERVIEW}
                      {...group}
                      showGroupMeta
                    />
                  ))
                }
              </div>
            </div>
            )
          : null
      }
      </div>
    )
  }
}

export default GroupsOverview
